import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  where
} from 'firebase/firestore'
import { db } from '../../db'
import {
  type Appointment,
  type AppointmentPayload,
  type AppointmentStatus,
  type AppointmentSettings
} from '../../model/appointment'
import {
  appointmentsOverlap,
  canTransitionAppointment,
  dateKeyInTimeZone,
  normalizeAppointmentSettings
} from '../../utils/appointmentUtils'
import { type AppointmentService } from './AppointmentService'
import { getHighestQueuePosition } from '../queue/queuePosition'

export class AppointmentServiceImpl implements AppointmentService {
  private static readonly collectionName = 'appointments'

  private async getLinkedQueueRefs(appointmentId: string) {
    const deterministicRef = doc(db, 'queue', `appointment_${appointmentId}`)
    return [deterministicRef]
  }

  subscribeToRange(
    ownerId: string,
    rangeStart: number,
    rangeEnd: number,
    onUpdate: (appointments: Appointment[]) => void,
    onError?: (error: Error) => void
  ): () => void {
    const rangeQuery = query(
      collection(db, AppointmentServiceImpl.collectionName),
      where('ownerId', '==', ownerId),
      where('start', '>=', rangeStart),
      where('start', '<', rangeEnd),
      orderBy('start')
    )
    return onSnapshot(
      rangeQuery,
      (snapshot) => {
        onUpdate(
          snapshot.docs.map((snapshotDoc) => ({
            id: snapshotDoc.id,
            ...(snapshotDoc.data() as Omit<Appointment, 'id'>)
          }))
        )
      },
      (error) => {
        if (onError) onError(error)
        else onUpdate([])
      }
    )
  }

  async findConflicts(
    ownerId: string,
    start: number,
    duration: number,
    excludeId?: string
  ): Promise<Appointment[]> {
    const conflictQuery = query(
      collection(db, AppointmentServiceImpl.collectionName),
      where('ownerId', '==', ownerId),
      where('start', '>=', start - 60 * 60_000),
      where('start', '<', start + duration * 60_000),
      orderBy('start')
    )
    const snapshot = await getDocs(conflictQuery)
    return snapshot.docs
      .map((snapshotDoc) => ({
        id: snapshotDoc.id,
        ...(snapshotDoc.data() as Omit<Appointment, 'id'>)
      }))
      .filter(
        (appointment) =>
          appointment.id !== excludeId &&
          appointmentsOverlap(start, duration, appointment)
      )
  }

  async createAppointment(
    appointment: AppointmentPayload,
    actorId: string
  ): Promise<Appointment> {
    const now = Date.now()
    const data: Omit<Appointment, 'id'> = {
      ...appointment,
      createdBy: actorId,
      updatedBy: actorId,
      createdAt: now,
      updatedAt: now
    }
    const created = await addDoc(
      collection(db, AppointmentServiceImpl.collectionName),
      data
    )
    return { id: created.id, ...data }
  }

  async updateAppointment(
    id: string,
    changes: Partial<AppointmentPayload>,
    actorId: string
  ): Promise<void> {
    if (changes.status === 'checked-in') {
      await this.checkInWithChanges(id, actorId, changes)
      return
    }
    if (changes.status === 'cancelled') {
      await this.cancelWithChanges(id, actorId, changes)
      return
    }

    await runTransaction(db, async (transaction) => {
      const appointmentRef = doc(db, AppointmentServiceImpl.collectionName, id)
      const snapshot = await transaction.get(appointmentRef)
      if (!snapshot.exists()) throw new Error('Appointment not found')
      const appointment = snapshot.data() as Omit<Appointment, 'id'>
      const status = changes.status ?? appointment.status
      if (!canTransitionAppointment(appointment.status, status)) {
        throw new Error(
          `Invalid appointment transition: ${appointment.status} -> ${status}`
        )
      }
      if (changes.ownerId && changes.ownerId !== appointment.ownerId) {
        throw new Error('Appointment owner cannot be changed')
      }
      transaction.update(appointmentRef, {
        ...changes,
        status,
        updatedBy: actorId,
        updatedAt: Date.now()
      })
    })
  }

  async updateStatus(
    id: string,
    status: AppointmentStatus,
    actorId: string
  ): Promise<void> {
    await this.updateAppointment(id, { status }, actorId)
  }

  async cancelAppointment(id: string, actorId: string): Promise<void> {
    await this.cancelWithChanges(id, actorId, {})
  }

  private async cancelWithChanges(
    id: string,
    actorId: string,
    changes: Partial<AppointmentPayload>
  ): Promise<void> {
    const queueRefs = await this.getLinkedQueueRefs(id)
    await runTransaction(db, async (transaction) => {
      const appointmentRef = doc(db, AppointmentServiceImpl.collectionName, id)
      const [appointmentSnapshot, ...queueSnapshots] = await Promise.all([
        transaction.get(appointmentRef),
        ...queueRefs.map(async (queueRef) => await transaction.get(queueRef))
      ])
      if (!appointmentSnapshot.exists()) {
        throw new Error('Appointment not found')
      }
      const appointment = appointmentSnapshot.data() as Omit<Appointment, 'id'>
      if (!canTransitionAppointment(appointment.status, 'cancelled')) {
        throw new Error(`Cannot cancel a ${appointment.status} appointment`)
      }
      queueSnapshots.forEach((queueSnapshot) => {
        if (
          queueSnapshot.exists() &&
          queueSnapshot.data().status === 'waiting'
        ) {
          transaction.delete(queueSnapshot.ref)
        }
      })
      transaction.update(appointmentRef, {
        ...changes,
        status: 'cancelled',
        updatedBy: actorId,
        updatedAt: Date.now()
      })
    })
  }

  async deleteAppointment(id: string): Promise<void> {
    const queueRefs = await this.getLinkedQueueRefs(id)
    await runTransaction(db, async (transaction) => {
      const appointmentRef = doc(db, AppointmentServiceImpl.collectionName, id)
      const [appointmentSnapshot, ...queueSnapshots] = await Promise.all([
        transaction.get(appointmentRef),
        ...queueRefs.map(async (queueRef) => await transaction.get(queueRef))
      ])
      if (appointmentSnapshot.exists()) transaction.delete(appointmentRef)
      queueSnapshots.forEach((queueSnapshot) => {
        if (queueSnapshot.exists()) transaction.delete(queueSnapshot.ref)
      })
    })
  }

  async checkIn(id: string, actorId: string): Promise<void> {
    await this.checkInWithChanges(id, actorId, {})
  }

  private async checkInWithChanges(
    id: string,
    actorId: string,
    changes: Partial<AppointmentPayload>
  ): Promise<void> {
    const appointmentRef = doc(db, AppointmentServiceImpl.collectionName, id)
    const initialAppointmentSnapshot = await getDoc(appointmentRef)
    if (!initialAppointmentSnapshot.exists()) {
      throw new Error('Appointment not found')
    }
    const initialAppointment = initialAppointmentSnapshot.data() as Omit<
      Appointment,
      'id'
    >
    const doctorSnapshot = await getDoc(
      doc(db, 'doctor', initialAppointment.ownerId)
    )
    const timezone = normalizeAppointmentSettings(
      doctorSnapshot.data()?.appointmentSettings as
        | Partial<AppointmentSettings>
        | undefined
    ).timezone
    const date = dateKeyInTimeZone(Date.now(), timezone)
    const highestStoredPosition = await getHighestQueuePosition(
      initialAppointment.ownerId,
      date
    )
    const queueRefs = await this.getLinkedQueueRefs(id)
    await runTransaction(db, async (transaction) => {
      const queueRef = doc(db, 'queue', `appointment_${id}`)
      const appointmentSnapshot = await transaction.get(appointmentRef)
      if (!appointmentSnapshot.exists()) {
        throw new Error('Appointment not found')
      }
      const appointment = appointmentSnapshot.data() as Omit<Appointment, 'id'>
      if (
        appointment.status !== 'scheduled' &&
        appointment.status !== 'confirmed' &&
        appointment.status !== 'checked-in'
      ) {
        throw new Error(`Cannot check in a ${appointment.status} appointment`)
      }

      if (changes.ownerId && changes.ownerId !== appointment.ownerId) {
        throw new Error('Appointment owner cannot be changed')
      }
      const appointmentStart = changes.start ?? appointment.start
      if (dateKeyInTimeZone(appointmentStart, timezone) !== date) {
        throw new Error(
          'Appointments can only be checked in on their clinic day'
        )
      }
      const counterRef = doc(
        db,
        'queueCounters',
        `${appointment.ownerId}_${date}`
      )
      const [counterSnapshot, ...queueSnapshots] = await Promise.all([
        transaction.get(counterRef),
        ...queueRefs.map(async (linkedRef) => await transaction.get(linkedRef))
      ])
      const queueSnapshot = queueSnapshots.find(
        (snapshot) => snapshot.ref.path === queueRef.path
      )

      // The deterministic queue ID makes retries and simultaneous check-ins safe.
      if (!queueSnapshot?.exists()) {
        const position =
          Math.max(
            highestStoredPosition,
            Number(counterSnapshot.data()?.nextPosition ?? 0)
          ) + 1
        transaction.set(queueRef, {
          doctorId: appointment.ownerId,
          patientId: changes.patientId ?? appointment.patientId,
          patientName: changes.patientName ?? appointment.patientName,
          date,
          addedAt: Date.now(),
          status: 'waiting',
          position,
          source: 'appointment',
          appointmentId: id,
          scheduledAt: appointmentStart
        })
        transaction.set(
          counterRef,
          {
            doctorId: appointment.ownerId,
            date,
            nextPosition: position,
            updatedAt: Date.now()
          },
          { merge: true }
        )
      }

      // Clean up any pre-deterministic linked rows so one appointment can
      // never occupy more than one queue position.
      queueSnapshots.forEach((snapshot) => {
        if (snapshot.exists() && snapshot.ref.path !== queueRef.path) {
          transaction.delete(snapshot.ref)
        }
      })

      if (
        appointment.status !== 'checked-in' ||
        Object.keys(changes).length > 0
      ) {
        transaction.update(appointmentRef, {
          ...changes,
          status: 'checked-in',
          updatedBy: actorId,
          updatedAt: Date.now()
        })
      }
    })
  }
}
